import type { DataStore } from "../data/DataStore";
import { DSZoneEngine } from "../chart/indicators/DSzone";
import { autoLevels, zoneGeometry } from "./zone";
import type { Levels, ZoneInfo, ZoneName } from "./types";

// Zone IDs are deterministic: <pattern>-<timeframe>-<signal candle time, base36>,
// e.g. "RBD-1m-SFZ9KW". The same zone always gets the same ID, and any ID can be
// turned back into the exact zone (re-running the indicator at that candle).

const RE = /^(DBR|RBR|RBD|DBD)-(\d+[smhd])-([0-9A-Z]+)$/i;

export const zoneIdOf = (type: ZoneName | string, tf: string, signalTime: number) => `${type}-${tf}-${Math.round(signalTime).toString(36).toUpperCase()}`;
export const idOfZone = (z: Pick<ZoneInfo, "type" | "signalTime" | "zoneId">, tf: string) => z.zoneId ?? zoneIdOf(z.type, tf, z.signalTime);

export function parseZoneId(raw: string): { type: ZoneName; tf: string; signalTime: number } | null {
  const m = raw.trim().match(RE);
  if (!m) return null;
  const signalTime = parseInt(m[3], 36);
  if (!Number.isFinite(signalTime)) return null;
  return { type: m[1].toUpperCase() as ZoneName, tf: m[2].toLowerCase(), signalTime };
}

export interface ResolvedZone {
  zone: ZoneInfo;
  auto: Levels;
  detectTf: string;
  detectTfSec: number;
  index: number;
}

export async function resolveZone(ds: DataStore, raw: string): Promise<ResolvedZone> {
  const p = parseZoneId(raw);
  if (!p) throw new Error("That doesn't look like a zone ID (e.g. RBD-1m-SFZ9KW)");
  const info = ds.tfInfo(p.tf);
  if (!info) throw new Error(`No ${p.tf} data in this app`);
  const store = await ds.getTimeframe(p.tf);
  const k = await store.lastIndexAtOrBefore(p.signalTime);
  await store.ensureRange(k - 160, k + 1, 0);
  if (k < 0 || store.timeAt(k) !== p.signalTime) throw new Error("No candle at that time — check the ID");
  // current rules first; IDs saved before the v2.15 update may only exist under the old v2.13 rules
  const find = (eng: DSZoneEngine) => eng.at(k)?.types.find((x) => x.name === p.type);
  const t = find(new DSZoneEngine(store, info.seconds)) ?? find(new DSZoneEngine(store, info.seconds, { v214: false, sw: false, minBoringBody: 0 }));
  if (!t) throw new Error(`The indicator doesn't find a ${p.type} zone on that candle`);
  const zone = zoneGeometry(store, k, t, p.tf);
  return { zone, auto: autoLevels(zone), detectTf: p.tf, detectTfSec: info.seconds, index: k };
}

// Small "copy on click" chip used wherever an ID is shown.
export const zoneIdChip = (id: string) => `<button class="zid" data-zid="${id}" title="Zone ID — click to copy">#${id}</button>`;

let wired = false;
export function wireZoneIdChips() {
  if (wired) return;
  wired = true;
  document.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-zid]");
    if (!b) return;
    e.stopPropagation();
    const id = b.dataset.zid!;
    navigator.clipboard?.writeText(id).catch(() => {});
    const old = b.textContent;
    b.textContent = "Copied ✓";
    setTimeout(() => (b.textContent = old), 900);
  });
}
